import React, { useEffect, useRef } from 'react';
import {
  Grid,
  makeStyles,
  Typography,
  Paper,
  Checkbox
} from '@material-ui/core';
import { useSelector, useDispatch } from 'react-redux';
import Table from '../../components/table/Table';
import View from '../../components/view/View';
import { show, hide } from 'redux-modal';
import colors from '../../styles/colors';
import { NewServerMessage } from '../../types/realtime/realtime-types';
import {
  initialzeRealTimeData,
  newUpdateRealTimeData
} from '../../redux/actions/realtime-actions';
import moment from 'moment';
import {
  Warning,
  Check,
  AccessTime,
  WifiOff,
  ThumbUpOutlined,
  Help,
  ChevronLeft
} from '@material-ui/icons';
import Button from '../../components/button/Button';
import { MODAL_DEFAULT } from '../../constants/modals';
import clsx from 'clsx';
import { isMobile } from 'react-device-detect';
import { useHistory } from 'react-router-dom';
import io, { Socket } from 'socket.io-client';

const useStyles = makeStyles(() => ({
  paper: {
    padding: '10px 20px',
    textAlign: 'right'
  },
  metricValue: {
    fontSize: 42
  },
  rowError: {
    '& td': {
      color: colors.error
    }
  },
  rowSuccess: {
    '& td': {
      color: colors.successDark
    }
  },
  alignCenter: {
    textAlign: 'center'
  },
  waitingIcon: {
    margin: '-1px 10px 0 0',
    verticalAlign: 'top'
  },
  closeButton: {
    position: 'absolute',
    right: 30,
    top: 20
  },
  closeButtonMobile: {
    left: 0,
    marginBottom: 30,
    position: 'relative',
    top: 0
  },
  helpRow: {
    alignItems: 'center',
    borderTop: '1px solid #dcdcdc',
    display: 'flex',
    marginTop: 10,
    paddingTop: 10
  },
  helpIcon: {
    marginRight: 10
  }
}));

const NewRealTimeView: React.FC<any> = (props: any) => {
  const classes = useStyles();
  const { metrics, players } = useSelector((state: any) => state.realTime);
  const dispatch = useDispatch();
  const currentClass = useSelector((state: any) => state.class.currentClass);
  const history = useHistory();
  let dontShowLeaveMessage = false;
  const questionWindowSocket = useRef<
    Socket<DefaultEventsMap, DefaultEventsMap>
  >();

  useEffect(() => {
    dispatch(initialzeRealTimeData(currentClass.roster));
    dontShowLeaveMessage = retrieveModalLocalStorage();
  }, []);

  useEffect(() => {
    questionWindowSocket.current = io(
      `${process.env.REACT_APP_REALTIME_WEBSOCKET_ENDPOINT_NEW}questionWindow`,
      {
        path: '/realtime/socket.io',
        query: {
          classCode: currentClass.code
        },
        withCredentials: true
      }
    );

    questionWindowSocket.current.on('connect', () => {
      console.log('Question window namespace connected');
    });

    questionWindowSocket.current.on('error', (err: any) => {
      console.log('Error', err);
    });

    questionWindowSocket.current.on('disconnect', () => {
      console.log('Question window namespace disconnected');
    });

    questionWindowSocket.current.on(
      'player-info',
      (playerData: NewServerMessage) => {
        const message: NewServerMessage = playerData;
        // console.log('player-info-message::', message);
        dispatch(newUpdateRealTimeData(message));
      }
    );

    return () => {
      questionWindowSocket.current?.disconnect();
      questionWindowSocket.current?.off('connect');
      questionWindowSocket.current?.off('disconnect');
      questionWindowSocket.current?.off('player-info');
    };
  }, [currentClass.code, dispatch]);

  const getRowClassName = (rowData: any) => {
    const totalQuestions = rowData.correct + rowData.incorrect;
    if (totalQuestions === 0) {
      return null;
    }
    if (rowData.correctPercentage < 50) {
      return classes.rowError;
    } else if (rowData.correctPercentage > 65) {
      return classes.rowSuccess;
    }
    return null;
  };

  const handleShowHelpModal = () => {
    dispatch(
      show('modal', {
        type: MODAL_DEFAULT,
        title: 'About the Real-Time Dashboard',
        children: () => (
          <>
            <Typography>
              Here, you can see your players progress in real time!
            </Typography>
            <Typography>
              While your players answer questions, you will see their
              information changing right away. Here is what you have to know
              while they play:
            </Typography>
            <div className={classes.helpRow}>
              <Warning
                style={{ color: colors.error }}
                className={classes.helpIcon}
              />
              <Typography>
                This icon means the player has answered 50% of the questions
                incorrectly. They are probably struggling and need some help.
              </Typography>
            </div>
            <div className={classes.helpRow}>
              <Check
                style={{ color: colors.successDark }}
                className={classes.helpIcon}
              />
              <Typography>
                This icon means the player has answered more than 65% of the
                questions correctly. They are doing great!
              </Typography>
            </div>
            <div className={classes.helpRow}>
              <ThumbUpOutlined
                style={{ color: '#636363' }}
                className={classes.helpIcon}
              />
              <Typography>
                This icon means the player is answering between 50% and 65% of
                questions correctly. They are doing okay!
              </Typography>
            </div>
            <div className={classes.helpRow}>
              <AccessTime className={classes.helpIcon} />
              <Typography>
                This icon means the dashboard is still waiting for the player to
                start answering questions.
              </Typography>
            </div>
          </>
        )
      })
    );
  };

  const setModalLocalStorage = (state: boolean) => {
    localStorage.setItem('realtime:modal', JSON.stringify(state));
  };

  const retrieveModalLocalStorage = () => {
    const savedState = localStorage.getItem('realtime:modal');
    return savedState ? JSON.parse(savedState) : false;
  };

  const handleLeaveRealTimeView = () => {
    if (dontShowLeaveMessage) {
      setModalLocalStorage(dontShowLeaveMessage);
    }
    history.goBack();
    dispatch(hide('modal'));
  };

  const handleModalLeaveCheckbox = (
    evt: React.ChangeEvent<HTMLInputElement>
  ) => {
    setModalLocalStorage(evt.target.checked);
  };

  const handleCloseDashboard = () => {
    if (retrieveModalLocalStorage()) {
      return handleLeaveRealTimeView();
    }
    dispatch(
      show('modal', {
        type: MODAL_DEFAULT,
        title: 'Leave Real-Time Dashboard',
        children: () => (
          <Typography>
            Are you sure you want to leave the real-time dashboard and stop
            receiving live data from your players? Data from today's Real-Time
            Session will be kept until midnight today.
          </Typography>
        ),
        leftActions: () => {
          return (
            <div>
              <Checkbox color="primary" onChange={handleModalLeaveCheckbox} />
              <span>Don't show me this message again</span>
            </div>
          );
        },
        actions: () => {
          return (
            <>
              <Button primary={true} onClick={handleLeaveRealTimeView}>
                Yes
              </Button>
            </>
          );
        }
      })
    );
  };

  return (
    <View
      title="New Real-Time Dashboard"
      subtitle={`(${moment().format('LL')})`}
      renderBackButton={() => (
        <Button onClick={handleCloseDashboard} style={{ marginRight: 10 }}>
          <ChevronLeft /> Back
        </Button>
      )}
    >
      <div
        className={clsx(
          classes.closeButton,
          isMobile && classes.closeButtonMobile
        )}
      >
        <Button onClick={handleShowHelpModal} style={{ marginRight: 10 }}>
          <Help style={{ color: '#636363' }} />
        </Button>
      </div>
      <Grid container spacing={4}>
        <Grid item xs={12} md={3}>
          <Paper className={classes.paper}>
            <Typography component="h4" className={classes.metricValue}>
              {metrics.totalPlaying}
            </Typography>
            <Typography component="h4">Players Playing</Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} md={3}>
          <Paper
            className={classes.paper}
            style={
              metrics.totalStruggling > 0
                ? { background: colors.error, color: colors.white }
                : {}
            }
          >
            <Typography component="h4" className={classes.metricValue}>
              {metrics.totalStruggling}
            </Typography>
            <Typography component="h4">Players Need Help</Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} md={3}>
          <Paper className={classes.paper}>
            <Typography component="h4" className={classes.metricValue}>
              {metrics.totalInactive}
            </Typography>
            <Typography component="h4">Inactive Players</Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} md={3}>
          <Paper className={classes.paper}>
            <Typography component="h4" className={classes.metricValue}>
              {metrics.totalQuestions}
            </Typography>
            <Typography component="h4">Questions Answered</Typography>
          </Paper>
        </Grid>
      </Grid>

      <Grid container spacing={4}>
        <Grid item xs={12}>
          <Table
            getRowProps={(row: any) => {
              const rowData = row.original;
              return {
                className: getRowClassName(rowData)
              };
            }}
            columns={[
              {
                Header: 'Status',
                Cell: (row: any) => {
                  const {
                    correctPercentage,
                    correct,
                    incorrect,
                    isOnline
                  } = row.row.original;
                  const totalQuestions = correct + incorrect;

                  if (!isOnline) return <WifiOff />;
                  if (totalQuestions === 0) {
                    return <AccessTime />;
                  }
                  if (correctPercentage < 50) {
                    return <Warning />;
                  } else if (correctPercentage > 65) {
                    return <Check />;
                  }
                  return <ThumbUpOutlined style={{ color: '#636363' }} />;
                }
              },
              {
                Header: 'Player Name',
                accessor: 'fullname'
              },
              {
                Header: 'Username',
                accessor: 'username'
              },
              {
                Header: (
                  <div style={{ textAlign: 'center' }}>Questions Answered</div>
                ),
                accessor: 'totalQuestions',
                Cell: (row: any) => {
                  let value = row.row.original.totalQuestions;
                  if (!value) {
                    value = (
                      <>
                        <AccessTime className={classes.waitingIcon} />
                        <span>Waiting for answers</span>
                      </>
                    );
                  }
                  return <div className={classes.alignCenter}>{value}</div>;
                }
              },
              {
                Header: <div className={classes.alignCenter}>Correct</div>,
                accessor: 'correct',
                Cell: (row: any) => (
                  <div className={classes.alignCenter}>
                    {row.row.original.correct || '-'}
                  </div>
                )
              },
              {
                Header: <div className={classes.alignCenter}>Incorrect</div>,
                accessor: 'incorrect',
                Cell: (row: any) => (
                  <div style={{ textAlign: 'center' }}>
                    {row.row.original.incorrect || '-'}
                  </div>
                )
              }
            ]}
            data={players?.sort(
              (a: any, b: any) => b.totalQuestions - a.totalQuestions
            )}
            defaultSize={500}
            hidePagination={false}
          />
        </Grid>
      </Grid>
    </View>
  );
};

export default NewRealTimeView;
